import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch } from 'react-redux';

import { MarketFiltersFields } from 'enums/market/market-filters-fields.enum';

import { publicSaleIn } from 'constants/Counters';
import { MARKET_CATEGORY_FILTERS } from 'constants/market.constant';

import { resetMarketDataAction } from 'actions/market.action';

import BadgeCheckbox from 'components/badgeCheckbox/BadgeCheckbox';
import Collapse from 'components/collapse/Collapse';
import DebounceInputFormField from 'components/debounceInputFormField/DebounceInputFormField';
import ElementLoader from 'components/element-loader/ElementLoader';
import HomeFooter from 'components/homeFooter/HomeFooter';
import SaleCounter from 'components/saleCounter/SaleCounter';
import SingleSelect from 'components/singleSelect/SingleSelect';

import { useNftsFilters } from 'hooks/market/useNftsFilters';
import { useEnv } from 'hooks/useEnv';
import { ScreenSize, useScreenSize } from 'hooks/useScreenSize';

import CloseIcon from 'assets/icons/close-icon.svg';

import PriceRangeField from '../priceRangeField/PriceRangeField';
import './MarketFilters.scss';

interface IMarketFiltersProps {
  children: JSX.Element;
}

const MarketFilters = ({ children }: IMarketFiltersProps) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const [showFilters, setShowFilters] = useState(false);

  const {
    filters,
    listElementRef,
    ordering,
    statuses,
    handleCategoryChange,
    handlePriceRangeChange,
    handleStatusChange,
    lazyLoadItems,
    updateFilters,
  } = useNftsFilters();

  const { useMarketMocks } = useEnv();

  useEffect(
    () => () => {
      dispatch(resetMarketDataAction());
    },
    []
  );

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <ElementLoader>
        <form className="market-filters">
          <div className={`market-filters__sidebar ${showFilters ? 'market-filters__sidebar--visible' : ''}`}>
            <Scrollbars>
              <div className="market-filters__sidebar-content">
                <div className="market-filters__sidebar-header">
                  <h1 className="market-filters__sidebar-title--hidden">Market</h1>
                  <h2 className="market-filters__sidebar-title">Filters</h2>
                  <button
                    type="button"
                    className="button--styleless market-filters__sidebar-close"
                    onClick={toggleFilters}
                  >
                    <img src={CloseIcon} alt="Close sidebar" />
                  </button>
                </div>
                <div className="market-filters__ordering">
                  <SingleSelect
                    ariaLabel="Order"
                    value={filters[MarketFiltersFields.ordering]}
                    onChange={(value) => updateFilters(MarketFiltersFields.ordering, value)}
                    options={ordering}
                  ></SingleSelect>
                </div>
                <Collapse title="Status">
                  <div className="market-filters__checkbox-list">
                    {statuses.map((status) => (
                      <div className="market-filters__checkbox-list-item" key={status.key}>
                        <BadgeCheckbox
                          title={status.value}
                          checked={filters[MarketFiltersFields.statuses].includes(status.key)}
                          onChange={(checked: boolean) => handleStatusChange(status.key, checked)}
                        ></BadgeCheckbox>
                      </div>
                    ))}
                  </div>
                </Collapse>
                <Collapse title="Category">
                  <SingleSelect
                    ariaLabel="Category"
                    placeholder="Category:"
                    value={filters[MarketFiltersFields.category]}
                    searchable={true}
                    options={MARKET_CATEGORY_FILTERS}
                    onChange={(option) => handleCategoryChange(option!)}
                  />
                </Collapse>
                <Collapse title="Price">
                  <PriceRangeField onChange={handlePriceRangeChange} />
                </Collapse>
              </div>
            </Scrollbars>
          </div>
          <div ref={listElementRef} className="market-filters__main">
            <Scrollbars onUpdate={lazyLoadItems} className="market-filters__scrollbar">
              <div className="market-filters__main-wrapper tw-flex tw-flex-col">
                <div className="market-filters__header">
                  <DebounceInputFormField
                    classNames="full-width input-outlined"
                    type="string"
                    placeholder="Search..."
                    value={filters[MarketFiltersFields.search]}
                    ariaLabel="search"
                    onChange={(event) => updateFilters(MarketFiltersFields.search, event.target.value)}
                  />
                  <SingleSelect
                    outlined
                    ariaLabel="Order"
                    value={filters[MarketFiltersFields.ordering]}
                    onChange={(value) => updateFilters(MarketFiltersFields.ordering, value)}
                    options={ordering}
                    searchable={false}
                  ></SingleSelect>
                  <button type="button" className="market-filters__show-more" onClick={toggleFilters}>
                    More filters
                  </button>
                </div>
                <div className="market-filters__content tw-flex tw-flex-1 tw-flex-col">{children}</div>
              </div>
              {screenSize < ScreenSize.mediumSmall && <HomeFooter className="market-list-footer" />}
            </Scrollbars>
          </div>
        </form>
        {useMarketMocks && (
          <div className="market-counter-overlay">
            <SaleCounter endAt={publicSaleIn} timerLabel="Public sale opens in" />
          </div>
        )}
      </ElementLoader>
    </>
  );
};

export default MarketFilters;
